import { Vue } from 'vue-property-decorator';
import store from '@/store/store';
import HttpRequest from '@/../bower_components/teknisavuecomponents/assets/utils/HttpRequest';
import SnackbarUtils from '@/../bower_components/teknisavuecomponents/assets/utils/SnackbarUtils';
import CreateRepository from '@/assets/ts/offline/repository/CreateRepository';
import SincronizacaoController from '@/assets/ts/controller/SincronizacaoController';
import PopupController from '@/assets/ts/controller/PopupController';
import router from '@/Router';
import LocalizacaoController from './LocalizacaoController';
import DynamicRepository from '../offline/repository/DynamicRepository';
import { OfflineTablesName } from '../offline/DynamicTables';
import EstruturaRepository from '../offline/repository/EstruturaRepository';
import PeriodoRepository from '../offline/repository/PeriodoRepository';
import Environment from '../configuration/environment';

// Informações necessárias para a tipagem
declare global {
  interface Window {
    cordova?: any;
    device?: {
      platform: string;
      version: string;
    };
  }

  interface Navigator {
    connection: {
      type: string;
    };
  }

  const Connection: {
    NONE: string;
    CELL: string;
    CELL_2G: string;
    CELL_3G: string;
    CELL_4G: string;
    UNKNOWN: string;
    WIFI: string;
    ETHERNET: string;
  };
}

/**
 * A classe OfflineController gerencia a conexão do dispositivo e operações offline, como sincronização e criação de banco de dados SQLite.
 * Esta classe também lida com eventos de mudança de estado de conectividade (online e offline).
 * 
 * Métodos:
 * 
 * - `init()`: Inicializa o banco de dados SQLite e configura eventos de conectividade.
 * - `getDB()`: Retorna a instância do banco de dados SQLite.
 * - `checkIsOffline()`: Verifica se o dispositivo está offline.
 * - `checkIsOnline()`: Verifica se o dispositivo está online.
 * - `isMobile()`: Verifica se o dispositivo é móvel.
 * - `isWeb()`: Verifica se o aplicativo está sendo executado em um navegador.
 */
export default class OfflineController extends Vue {
  private static db: any;
  private static isOffline: boolean = false;
  private static isOfflineAbsolute: boolean = false;
  private static timeIntervalSync = 300000;

  private static messages = {
    offline: 'Você está offline no momento.',
    online: 'Sua conexão com a internet foi restaurada.',
  }
  private static icons = {
    wifi: 'mdi-wifi',
    wifiOff: 'mdi-wifi-off'
  }

  /**
   * Inicializa o banco de dados SQLite e configura eventos de conectividade.
   * 
   * - Evento 'offline': Disparado quando o dispositivo fica offline. Atualiza a flag isOffline e mostra uma mensagem.
   * - Evento 'online': Disparado quando o dispositivo fica online. Atualiza a flag isOffline e inicia o fluxo de sincronização.
  */
  static init() {

    document.addEventListener('deviceready', async () => {
      if(this.isWeb()) return;

      this.db = window.sqlitePlugin.openDatabase({ 
        name: 'app.db',
        location: 'default'
      });

      await CreateRepository.create();
      this.setGeoLocationForOffline();

      // Inicialize isOffline após deviceready
      this.isOffline = navigator.connection.type === window.Connection.NONE;
      this.isOfflineAbsolute = this.isOffline;

      // Inicia o Fluxo para quando o Portal Fica Offline
      document.addEventListener('offline', () => {
        this.setOffline();
        this.isOfflineAbsolute = true;

        SnackbarUtils.showMessage(
          this.messages.offline,
          SnackbarUtils.TYPE_INFO,
          5000,
          this.icons.wifiOff,
          { x: 'center', y: 'bottom' }
        );

      }, false);

      // Inicia o Fluxo para quando o Portal Fica Online
      document.addEventListener('online', () => {
        this.setOnline();
        this.isOfflineAbsolute = false;
  
        SnackbarUtils.showMessage(
          this.messages.online,
          SnackbarUtils.TYPE_INFO,
          5000,
          this.icons.wifi,
          { x: 'center', y: 'bottom' }
        );

        SincronizacaoController.iniciarFluxoOnline();

        this.confirmPassword();

      }, false);

    }, false);

    setInterval( async () => {
      await SincronizacaoController.iniciarFluxoOnline();
    }, (1000 * 60 * 120));

  }

  /**
   * Retorna a instância do banco de dados SQLite.
   * @returns A instância do banco de dados SQLite.
  */
  public static getDB(): any {
    return this.db;
  }

  /**
  * Verifica a conexão com a internet tentando acessar um endpoint confiável.
  */
  static async checkInternetConnection() {
    try {
      const response = await HttpRequest.get('checkInternetConnection', {
        requestType: 'FilterData'
      }, false, null, true, null, 5000);
      
      if(response.data.dataset.data.valor){
        this.setOnline();
      }
    } catch (error) {
      this.setOffline();
      //SnackbarUtils.showMessage('Sem conexão: o dispositivo entrou em modo offline.', SnackbarUtils.TYPE_INFO);
    }
  }

  /**
   * Verifica se o dispositivo está offline (em alguns casos caso a internet esteja lenta (DIFERENTE DO ISOFFLINEABSOLUTE), pode ser considerador offline
   * como pode exemplo na rotina de registro de ponto, na qual caso demore mais de X segundos o app é setado para offline).
   * @returns Verdadeiro se o dispositivo estiver offline e for um dispositivo móvel.
  */
  public static checkIsOffline(): boolean {
    return this.isOffline && this.isMobile();
  }

  /**
   * Verifica se o dispositivo está online.
   * @returns Verdadeiro se o dispositivo estiver online.
  */
  public static checkIsOnline(): boolean {
    return !this.isOffline;
  }

  /**
   * Verifica se o dispositivo está completamente offline, ignorando o estado de conectividade lenta (DIFERENTE DO ISOFFLINE).
   * 
   * @returns Verdadeiro se o dispositivo estiver completamente offline (sem qualquer conexão ativa).
  */
  public static checkIsOfflineAbsolute(): boolean {
    return this.isOfflineAbsolute && this.isMobile();
  }

  /**
   * Verifica se o dispositivo é móvel.
   * @returns Verdadeiro se o dispositivo for móvel.
  */
  public static isMobile(): boolean {
    return window.cordova && window.cordova.platformId && window.cordova.platformId.toLowerCase() !== 'browser';
  }

  /**
   * Verifica se o aplicativo está sendo executado em um navegador.
   * @returns Verdadeiro se o aplicativo estiver sendo executado em um navegador.
  */
  public static isWeb(): boolean {
    return !this.isMobile();
  }

  /**
   * Define manualmente o estado do dispositivo como offline.
   * Mesmo que o dispositivo esteja conectado à rede através de dados móveis ou Wi-Fi,
   * a verificação padrão pode indicar que está online, mesmo quando não há acesso real à internet.
   * Este método força o sistema a considerar o dispositivo como offline, ignorando o estado de conexão de rede.
   * Use este método para garantir que a aplicação trate o dispositivo como offline, mesmo quando a conexão
   * está ativa mas sem acesso à internet, como quando os dados móveis estão ligados sem uma conexão válida.
  */
  public static setOfflineManually() {
    if(this.isMobile()) {
      this.setOffline();
    }
  }

  private static confirmPassword() {
    const route = router.currentRoute.path;

    if(route !== '/registroPonto' && route !== '/login') {
      PopupController.openConfirmPasswordPopUp(null, true);
    };
  }

  /**
   * Atualiza a localização geográfica do usuário para uso offline.
   * 
   * Esta função obtém a localização atual do usuário e a armazena em uma tabela offline.
   * Se a aplicação estiver rodando em um ambiente web, o processo será interrompido.
   * Caso contrário, a localização é obtida e salva em uma tabela dinâmica de armazenamento local.
   * 
   * @returns {Promise<void>} Sem retorno (função assíncrona).
  */
  public static async setGeoLocationForOffline() {
    try {

      if(OfflineController.isWeb()) return;

      const localizacao = await LocalizacaoController.getGeoLocation(false);

      const row = [
        {
          LATITUDE: localizacao.latitude,
          LONGITUDE: localizacao.longitude
        }
      ];

      if(localizacao) {
        await DynamicRepository.deleteDynamicTable(OfflineTablesName.USER_LOCATION);
      }

      await DynamicRepository.setDynamicTableValues(OfflineTablesName.USER_LOCATION, row);

    } catch (error) {
      console.log('Erro ao atualizar a localização');
    }
  }

  public static setOffline() {
    this.isOffline = true;
    store.commit('setIsOffline', true);
  }

  public static setOnline() {
    this.isOffline = false;
    store.commit('setIsOffline', false);
  }

  /**
   * Verifica se existem dados de cache disponíveis nas tabelas de estruturas e períodos.
   * Esta função faz uma verificação simultânea nas tabelas de estruturas e períodos,
   * determinando se ambas possuem registros disponíveis.
   * 
   * @returns {Promise<boolean>} Verdadeiro se ambas as tabelas de estruturas e períodos contiverem registros.
  */
  public static async hasCachedData(): Promise<boolean> {

    if(this.isWeb) return false;

    try {
      const [estruturas, periodos] = await Promise.all([
        EstruturaRepository.getEstruturas(),
        PeriodoRepository.getPeriodos()
      ]);
  
      if(estruturas.length && periodos.length) {
        return true;
      }
  
      return false;
    } catch (error) {
      return false;
    }
  }

  /**
   * Verifica a velocidade da internet medindo o tempo de resposta da requisição Environment.getLoginInfo().
   * Se a requisição demorar mais do que o limite (20 segundos), considera a internet lenta.
  */
  public static async checkInternetSpeed(): Promise<void> {
    const timeoutLimit = 20000;
    const startTime = Date.now();

    try {
      // Cria um Promise com timeout
      const response = await Promise.race([
        Environment.getLoginInfo(),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error("Timeout: Conexão lenta")), timeoutLimit)
        ),
      ]);

      const duration = Date.now() - startTime; // Calcula o tempo de resposta

      console.log(`Requisição completada em ${duration} ms`);

      if (duration > timeoutLimit) {
        console.warn("A internet está lenta!");
        this.setOffline(); // Muda para offline se necessário
      } else {
        console.log("Velocidade aceitável.");
        this.setOnline(); // Confirma que está online
      }
    } catch (error) {
      console.error("Erro ao medir a velocidade da internet:", error);
      this.setOffline(); // Considera offline em caso de erro
    }
  }

}
